import { useEffect, useState, useCallback } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { DataTable, Badge } from "@/components/common/DataTable";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";

export default function LanguagesTab() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canEdit = isSuperAdmin || hasPermission("LANGUAGE.EDIT");
  const canCreate = isSuperAdmin || hasPermission("LANGUAGE.CREATE");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", nativeName: "", direction: "ltr", fallbackCode: "en" });

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/v1/i18n/admin/languages")).data.data.items); }
    catch { setItems([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (lng) => {
    try {
      await api.put(`/v1/i18n/admin/languages/${lng.code}`, { enabled: !lng.enabled });
      toast.success(`${lng.name} ${!lng.enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const create = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Code and name required"); return; }
    try {
      await api.post("/v1/i18n/admin/languages", form);
      toast.success("Language added");
      setShowForm(false);
      setForm({ code: "", name: "", nativeName: "", direction: "ltr", fallbackCode: "en" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="languages-tab">
      <div className="flex justify-end mb-3">
        {canCreate && (
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 py-1.5" data-testid="add-language-btn">
            <Plus className="h-4 w-4" /> Add language
          </button>
        )}
      </div>
      <DataTable testid="languages-table" rows={items} loading={loading}
        columns={[
          { key: "name", header: "Language", render: (l) => <div><div className="font-medium text-foreground">{l.nativeName || l.name}</div><div className="text-xs text-muted-foreground">{l.name} · {l.code}</div></div> },
          { key: "direction", header: "Direction", render: (l) => l.direction === "rtl" ? <Badge color="violet">RTL</Badge> : <span className="text-xs text-muted-foreground">LTR</span> },
          { key: "isDefault", header: "Default", render: (l) => l.isDefault ? <Badge color="teal">Default</Badge> : "—" },
          { key: "fallbackCode", header: "Fallback", render: (l) => <span className="text-xs">{l.fallbackCode || "—"}</span> },
          { key: "enabled", header: "Enabled", render: (l) => <Switch checked={!!l.enabled} onCheckedChange={() => toggle(l)} disabled={l.isDefault || !canEdit} data-testid={`toggle-lang-${l.code}`} /> },
        ]} />

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="add-language-modal">
          <div className="bg-card rounded-xl border border-border w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground">Add language</h3>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Code (BCP-47, e.g. ja)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="lang-code-input" />
              <input placeholder="Name (e.g. Japanese)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="lang-name-input" />
              <input placeholder="Native name (日本語)" value={form.nativeName} onChange={(e) => setForm({ ...form, nativeName: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                <option value="ltr">Left-to-right</option>
                <option value="rtl">Right-to-left</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="rounded-lg border border-border px-3 py-1.5 text-sm">Cancel</button>
              <button onClick={create} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 text-sm" data-testid="submit-language-btn">Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
